import styled from "@emotion/styled";
import {
  type Customer,
  getCustomerDetails,
  isIcCustomer,
  resetCustomerProfile,
} from "@repo/api";
import {
  Badge,
  Button,
  Icon,
  LightMode,
  Skeleton,
  Text,
  Tooltip,
  Variables,
  arrowRightIcon,
  briefcaseIcon,
  informationCircleIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  getTimezoneRegionByTimezone,
  maskEmail,
  maskPhoneNumber,
  trackAnalytics,
} from "@repo/utils";

import { useSettings } from "../settings/SettingsProvider";
import { environment } from "../settings/environment";
import {
  type CustomerSource,
  getCustomerLanguage,
  getCustomerName,
  getCutomerSourceLabel,
  getFormattedAddress,
} from "../utils/customerHelpers";
import { translateLanguage } from "../utils/translationHelpers";
import { ButtonsContainer } from "./ButtonContainer";
import { CardContainer } from "./CardContainer";
import { FlexDiv } from "./FlexDiv";
import { MenuButton } from "./MenuButton";
import { OneLineInfo } from "./OneLineInfo";
import { PaddedContainer } from "./PaddedContainer";
import { useToast } from "./Toast";

interface CustomerCardProps {
  editable?: boolean;
  customer?: Customer;
  isValidating?: boolean;
  customerSource?: CustomerSource;
  setCustomerTimezone?: (timezone: string) => void;
}

enum PreferredContactType {
  EMAIL = "EMAIL",
  PHONE = "PHONE",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

const CustomerContactWrapper = styled.div`
  display: flex;
  gap: ${Variables.space50};
`;

const CustomerContact = ({
  email,
  phoneNumber,
  showBadge = false,
}: {
  email?: string;
  phoneNumber?: string;
  showBadge?: boolean;
}) => {
  const { customerMode } = useSettings();
  const contact = email
    ? customerMode
      ? maskEmail(email)
      : email
    : phoneNumber
      ? customerMode
        ? maskPhoneNumber(phoneNumber)
        : phoneNumber
      : "";

  return (
    <CustomerContactWrapper>
      <Text tagName="p">{contact}</Text>
      {showBadge && <Badge label={t("label.preferred")} size="small" />}
    </CustomerContactWrapper>
  );
};

const getPreferredContactType = (customer?: Customer) => {
  switch (customer?.preferredContactType) {
    case "EMAIL":
      return PreferredContactType.EMAIL;
    case "PHONE":
    case "MOBILE":
    case "SMS":
      return PreferredContactType.PHONE;

    default:
      return PreferredContactType.NOT_AVAILABLE;
  }
};

export const CustomerCard = ({
  editable = true,
  customer,
  isValidating,
  customerSource,
}: CustomerCardProps) => {
  const { general, market, customerMode } = useSettings();
  const { updateToast } = useToast();

  const customerSourceLabel = getCutomerSourceLabel(customerSource);
  const preferredContactType = getPreferredContactType(customer);

  const hasMultipleTimezones =
    customer?.timezone && customer.timezone.length > 1;

  const customerName =
    getCustomerName(
      {
        firstName: customer?.firstName,
        lastName: customer?.lastName,
        customerType: customer?.customerType,
        organizationName: customer?.organizationName,
      },
      general?.localization?.reverse_names,
      customerMode
    ) ?? t("prompt.customer_not_found");

  const isBusinessCustomer = customer?.customerType === "Business";

  const registeredLabel = () => {
    if (customer) {
      if (isIcCustomer(customer?.partyId)) {
        return (
          <Text
            textStyle="Heading.XS"
            color={LightMode.colourCommercialMessageIkeaFamily}
          >
            {t("label.invoice_customer")}
          </Text>
        );
      }

      if (customer.familyNumber) {
        return (
          <Text
            textStyle="Heading.XS"
            color={LightMode.colourCommercialMessageIkeaFamily}
          >
            {isBusinessCustomer
              ? `${t("label.business_network_number")} ${customer.familyNumber}`
              : `${t("label.family_number")} ${customer.familyNumber}`}
          </Text>
        );
      }

      if (customer.customerAccountState === "REGISTERED") {
        return (
          <Text color={LightMode.colourStaticIkeaFamily} textStyle="Heading.XS">
            <T translationKey="label.registered" />
          </Text>
        );
      }
    }

    return null;
  };

  const onClick = () => {
    resetCustomerProfile();
    getCustomerDetails(
      customer?.partyId ?? "",
      customer?.country ?? market,
      customer?.customerType ?? "Individual",
      updateToast
    );
    trackAnalytics("open_customer_card");
  };

  return (
    <CardContainer
      backgroundColor={LightMode.colourNeutral2}
      onClick={editable ? onClick : undefined}
    >
      <PaddedContainer height="100%">
        {isValidating && !customer && (
          <Skeleton
            height="80px"
            width="100%"
            data-testid="customer-card-skeleton"
          />
        )}

        {!isValidating && !customer && (
          <Text textStyle="Body.M" color={LightMode.colourNeutral7}>
            {t("prompt.no_customer_info")}
          </Text>
        )}

        {customer && (
          <FlexDiv gap="10px" justifyContent="space-between" height="100%">
            <FlexDiv flexDirection="column" gap={Variables.space25}>
              {registeredLabel()}
              <FlexDiv flexDirection="column" gap={Variables.space50}>
                <Text textStyle="Heading.S">{customerName}</Text>
                <OneLineInfo
                  divider=", "
                  info={getFormattedAddress(
                    customer,
                    general?.localization?.use_address_format,
                    customerMode
                  )}
                />
                {customer.email && (
                  <CustomerContact
                    email={customer.email}
                    showBadge={
                      preferredContactType === PreferredContactType.EMAIL
                    }
                  />
                )}
                {customer.phoneNumber && (
                  <CustomerContact
                    phoneNumber={customer.phoneNumber}
                    showBadge={
                      preferredContactType === PreferredContactType.PHONE
                    }
                  />
                )}
                {customer.timezone && (
                  <FlexDiv gap={Variables.space25} alignItems="center">
                    <Text textStyle="Body.M">{`${t("label.timezone")}: ${getTimezoneRegionByTimezone(market, customer.timezone[0])}`}</Text>
                    {hasMultipleTimezones && (
                      <Tooltip
                        tooltipText={t("prompt.multiple_timezone_info")}
                        describedById="multiple-timezone-tooltip"
                      >
                        <Icon paths={informationCircleIcon} />
                      </Tooltip>
                    )}
                  </FlexDiv>
                )}
                {customer?.language && (
                  <Text textStyle="Body.S">
                    {translateLanguage(getCustomerLanguage(customer.language))}
                  </Text>
                )}
                {customer?.firstName && customerSourceLabel && (
                  <Text textStyle="Body.S">{customerSourceLabel}</Text>
                )}
              </FlexDiv>
            </FlexDiv>
            {editable && (
              <ButtonsContainer justifyContent="end">
                {isBusinessCustomer && (
                  <MenuButton
                    menuItems={[
                      {
                        icon: briefcaseIcon,
                        title: t("action.open_b2b_customer"),
                        disabled: false,
                        onClick: () => {
                          const businessCustomerId = customer.bcmId;
                          const businessOwnerId = customer.partyId;

                          const baseUrl = environment.isProd
                            ? "https://customeradmin.customer.ingka.com"
                            : "https://customeradmin.test.customer.ingka.com";

                          const url = `${baseUrl}/${market.toLocaleUpperCase()}/business/${businessCustomerId}/contact-person/${businessOwnerId}/start`;

                          window.open(url, "_blank");
                          trackAnalytics("open_b2b_customer");
                        },
                      },
                    ]}
                  />
                )}
                <Button
                  size="small"
                  iconOnly
                  ssrIcon={arrowRightIcon}
                  type="primary"
                  text={t("label.customer_details")}
                />
              </ButtonsContainer>
            )}
          </FlexDiv>
        )}
      </PaddedContainer>
    </CardContainer>
  );
};
